import React from "react";

import { useQuery } from "@tanstack/react-query";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import StylesAPI from "@/src/api/StylesAPI";
import FontsPanel from "@/src/components/appearance/fonts/FontsPanel";
import AppearancePreview from "@/src/components/appearance/preview/AppearancePreview";
import { useProjectStyles } from "@/src/hooks/useProjectStyles";
import useUrlTab from "@/src/hooks/useUrlTab";
import { cn } from "@/src/lib/utils";
import { toPreviewFonts } from "@/src/utils/fontFaces";

import AppearanceHeader from "./AppearanceHeader";
import LoadErrorAlert from "./LoadErrorAlert";
import SectionList from "./SectionList";
import SectionPanel from "./SectionPanel";
import { FONTS_SECTION_ID, OPENABLE_SECTION_ID_LIST } from "./sections";

import type { JSX } from "react";

import type { ProjectStyles } from "@/src/api/StylesAPI.types";

// Les `resetOptions` du formulaire s'appliquent à tout `reset` : on les annule pour repartir à neuf.
const DISCARD_DRAFT_OPTIONS = { keepDirtyValues: false, keepDirty: false };

function AppearanceIndex(): JSX.Element {
  const { t } = useTranslation("editor-appearance");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const {
    styles,
    isLoading: isLoadingStyles,
    isError: isStylesError,
    isSaving,
    refetch: refetchStyles,
    saveStyles,
  } = useProjectStyles(projectSlug);
  const {
    data: availableFontData,
    isLoading: isLoadingFamilyList,
    isLoadingError: isFamilyListError,
    refetch: refetchFamilyListQuery,
  } = useQuery(
    StylesAPI.generateQuery({
      fn: StylesAPI.getAvailableFontList,
      params: [],
    }),
  );
  const familyList = availableFontData?.families ?? [];
  const isLoading = isLoadingStyles || isLoadingFamilyList;
  const isRoleEditionDisabled = isLoadingStyles || isStylesError || isSaving;

  // `keepDirty` : sans lui, `keepDirtyValues` remet `isDirty` à false en gardant les valeurs modifiées.
  const form = useForm<ProjectStyles>({
    values: styles,
    resetOptions: { keepDirtyValues: true, keepDirty: true },
  });
  const currentRoles = useWatch({ control: form.control, name: "fonts" });
  const { isDirty } = form.formState;

  const { activeTab: openSectionId, setActiveTab: setOpenSectionId } =
    useUrlTab({
      availableTabs: OPENABLE_SECTION_ID_LIST,
      defaultTab: null,
      paramKey: "section",
    });

  const refetchFamilyList = (): void => {
    refetchFamilyListQuery().catch(() => undefined);
  };

  const handleValidSubmit = async (values: ProjectStyles): Promise<void> => {
    try {
      await saveStyles(values);
      form.reset(values, DISCARD_DRAFT_OPTIONS);
      toast.success(t("saveSuccess"));
    } catch {
      toast.error(t("saveError"));
    }
  };

  const handleCancel = (): void => {
    form.reset(undefined, DISCARD_DRAFT_OPTIONS);
  };

  // Le serveur refuse de supprimer une police utilisée : seul le brouillon peut la porter.
  const handleFontDeleted = (fontUuid: string): void => {
    if (form.getValues("fonts.title") === fontUuid) {
      form.resetField("fonts.title");
    }
    if (form.getValues("fonts.text") === fontUuid) {
      form.resetField("fonts.text");
    }
  };

  const handleBack = (): void => {
    setOpenSectionId(null);
  };

  // Rien n'est envoyé avant que les polices soient connues, sinon le storefront retirerait celles publiées.
  const previewState =
    isLoading || isStylesError || isFamilyListError
      ? null
      : { fonts: toPreviewFonts(currentRoles, familyList) };

  const isSectionOpen = openSectionId === FONTS_SECTION_ID;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <div className="flex size-full min-h-0 flex-col">
        <AppearanceHeader
          isDirty={isDirty}
          isSaving={isSaving}
          onCancel={handleCancel}
          onSave={form.handleSubmit(handleValidSubmit)}
        />
        <div className="flex min-h-0 flex-1">
          <div className="bg-card flex w-[372px] shrink-0 flex-col border-r">
            {isStylesError ? (
              <div className="shrink-0 p-3">
                <LoadErrorAlert
                  message={t("loadStylesError")}
                  onRetry={refetchStyles}
                />
              </div>
            ) : null}
            <div className="relative min-h-0 flex-1 overflow-hidden">
              <div
                data-testid="section-list-layer"
                inert={isSectionOpen}
                className={cn(
                  "absolute inset-0 flex flex-col overflow-y-auto transition-[transform,opacity] duration-200 ease-[cubic-bezier(.4,0,.2,1)]",
                  isSectionOpen
                    ? "pointer-events-none -translate-x-[14%] opacity-0"
                    : "translate-x-0 opacity-100",
                )}
              >
                <SectionList
                  familyList={familyList}
                  isFontsLoading={isLoading}
                  onOpenSection={setOpenSectionId}
                  roles={currentRoles}
                />
              </div>
              <div
                data-testid="section-panel-layer"
                inert={!isSectionOpen}
                className={cn(
                  "bg-card absolute inset-0 flex flex-col transition-transform duration-200 ease-[cubic-bezier(.4,0,.2,1)]",
                  isSectionOpen
                    ? "translate-x-0"
                    : "pointer-events-none translate-x-full",
                )}
              >
                <SectionPanel onBack={handleBack} title={t("fontsTab")}>
                  <FontsPanel
                    familyList={familyList}
                    isDisabled={isRoleEditionDisabled}
                    isFamilyListError={isFamilyListError}
                    isLoading={isLoading}
                    onFontDeleted={handleFontDeleted}
                    onRetryFamilyList={refetchFamilyList}
                    roles={currentRoles}
                  />
                </SectionPanel>
              </div>
            </div>
          </div>
          <div className="bg-background min-w-0 flex-1 overflow-hidden">
            <AppearancePreview state={previewState} />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

export default AppearanceIndex;
